import { useRef, useState } from "react";
import { Send, Image as ImageIcon, X } from "lucide-react";
import { toast } from "react-toastify";
import {
  createPost,
  generatePostId,
  uploadPostImage,
} from "../../utils/firebasefunctions";
import { useStateValue } from "../../context/StateProvider";
import {
  extractMentionedIds,
  useMentionAutocomplete,
  getFullName,
} from "../../utils/mentions";
import { newmessagesuccess } from "../../utils/helper";

function initialsFor(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return ((parts[0] || "?")[0] || "?").toUpperCase().padEnd(2, "?");
}

// Props:
//   currentUser: { id, firstname, lastname, role, ... }
//   onCreated: (newPost) => void   — called after a post is created (parent dispatches into context)
function PostComposer({ currentUser, onCreated }) {
  const [{ users }] = useStateValue();
  const [text, setText] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const mention = useMentionAutocomplete({
    text,
    setText,
    users,
    currentUserId: currentUser?.id,
    fieldRef: textareaRef,
  });

  const displayName =
    `${currentUser?.firstname || ""} ${currentUser?.lastname || ""}`.trim();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Image must be under 10 MB.");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if ((!trimmed && !imageFile) || submitting) return;
    setSubmitting(true);
    try {
      const postId = generatePostId();
      let imageUrls = [];
      if (imageFile) {
        const url = await uploadPostImage(imageFile, postId);
        imageUrls = [url];
      }
      const newPost = await createPost({
        id: postId,
        authorId: currentUser.id,
        authorName: displayName,
        authorRole: currentUser?.role || "",
        authorPhoto: currentUser?.profilepicture || "",
        text: trimmed,
        imageUrls,
      });
      // Notify anyone @-mentioned in the post.
      const mentionedIds = [...extractMentionedIds(trimmed, users)];
      if (mentionedIds.length > 0) {
        const senderName = getFullName(currentUser) || "Someone";
        await Promise.all(
          mentionedIds.map((id) =>
            newmessagesuccess(
              "You were mentioned",
              id,
              `${senderName} mentioned you in a post`,
              postId,
              "mention",
              postId,
            ).catch((err) =>
              console.error("Mention notification failed:", err),
            ),
          ),
        );
      }
      onCreated?.(newPost);
      setText("");
      removeImage();
    } catch (err) {
      console.error("Failed to create post:", err);
      toast.error("Failed to post");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white border border-black/[0.06] rounded-xl shadow-sm p-5 mb-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-slate-700 text-white text-[12px] font-bold flex items-center justify-center flex-shrink-0">
          {initialsFor(displayName)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={mention.fieldHandlers.onChange}
              placeholder="Share something with the team… (type @ to mention)"
              rows={3}
              className="w-full resize-y border border-gray-200 rounded-md px-3 py-2 text-[13.5px] text-slate-800 placeholder-gray-400 focus:outline-none focus:border-slate-400"
            />
            {mention.showMentions && mention.candidates.length > 0 && (
              <div
                className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-[200px] overflow-y-auto z-30"
                onMouseDown={(e) => e.preventDefault()}
              >
                {mention.candidates.map((u) => (
                  <button
                    type="button"
                    key={u.id}
                    onClick={() => mention.selectMention(u)}
                    className="w-full text-left p-2 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <img
                      src={u.profilepicture || "/assets/user.png"}
                      alt=""
                      className="w-6 h-6 rounded-full object-cover"
                    />
                    <span className="text-sm text-slate-800">
                      {getFullName(u)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {imagePreview && (
            <div className="relative mt-2 inline-block">
              <img
                src={imagePreview}
                alt="preview"
                className="max-h-48 rounded-md border border-gray-200"
              />
              <button
                type="button"
                onClick={removeImage}
                className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-1"
                aria-label="Remove image"
              >
                <X size={12} />
              </button>
            </div>
          )}

          <div className="flex items-center justify-between mt-2">
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-600 hover:text-slate-900 px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50"
              >
                <ImageIcon size={14} />
                {imageFile ? "Replace image" : "Add image"}
              </button>
            </div>
            <button
              type="submit"
              disabled={(!text.trim() && !imageFile) || submitting}
              className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-4 py-1.5 rounded-md text-[12.5px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              <Send size={13} />
              {submitting ? "Posting…" : "Post"}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

export default PostComposer;
